import React, { createContext, useContext, useEffect } from 'react';

const ThemeContext = createContext(null);

/**
 * LifeFlo AI Brand Colors
 * Trust, Calm, Clarity, Premium
 * 
 * Primary: Soft Teal #2FB9B3 - Flow, Life, Continuity
 * Background: Off-White #F7F9F8 - Reduces eye fatigue, modern & premium
 * AI Accent: Deep Indigo #3A4F7A - Thinking, intelligence, depth
 * Emotional: Soft Coral #F28C8C - Human warmth
 * Action/CTA: Fresh Mint #6EDBC8 - Positive, encouraging
 */

const THEME = {
  id: 'lifeflo',
  name: 'LifeFlo',
  description: 'Trust, Calm, Clarity, Premium',
  colors: {
    // Core Brand Color
    primary: '#2FB9B3',        // Soft Teal - main brand color
    primaryLight: '#4DC9C4',
    primaryDark: '#239490',
    
    // AI Intelligence Color
    ai: '#3A4F7A',             // Deep Indigo - AI chat, insights
    aiLight: '#4A6090',
    aiDark: '#2A3F5A',
    
    // Action/CTA Color
    action: '#6EDBC8',         // Fresh Mint - buttons, CTAs
    actionLight: '#8FE8D8',
    actionDark: '#4ECBB8',
    
    // Emotional/Life Color
    emotional: '#F28C8C',      // Soft Coral - life mode, journaling
    emotionalLight: '#F5A5A5',
    emotionalDark: '#E87373',
    
    // Work Mode Color
    work: '#5B6E8C',           // Slate Blue
    workLight: '#7A8BA3',
    workDark: '#4A5D7A',
    
    // Health Mode Color  
    health: '#6EDBC8',         // Mint Green (same as action)
    healthLight: '#8FE8D8',
    healthDark: '#4ECBB8',
    
    // Backgrounds
    background: '#F7F9F8',     // Off-White / Soft Sand
    backgroundDark: '#0F1E1E', // Charcoal Navy (for dark mode)
    cardBg: '#FFFFFF',
    
    // Legacy mappings for compatibility
    accent: '#3A4F7A',         // AI Indigo as accent
    accentLight: '#4A6090',
    
    // UI Elements
    gradient: 'from-teal-500 to-cyan-400',
    gradientBg: 'from-[#F7F9F8] to-white',
    cardBorder: 'border-teal-100',
    buttonBg: 'bg-[#2FB9B3] hover:bg-[#239490]',
    buttonText: 'text-white',
    textAccent: 'text-[#2FB9B3]',
    iconColor: 'text-[#2FB9B3]',
    logo: '#2FB9B3',
  },
  // Mode-specific colors for visual distinction
  modes: {
    home: '#2FB9B3',    // Soft Teal
    chat: '#3A4F7A',    // Deep Indigo (AI)
    health: '#6EDBC8',  // Mint Green
    work: '#5B6E8C',    // Slate Blue
    life: '#F28C8C',    // Soft Coral
  },
  emojis: {
    gentle: '🌊✨🫂',
    neutral: '✨😊💫',
    motivational: '🎯✨💪',
    heart: '🩵',       // Light blue heart for teal theme
    star: '✨',
    fire: '⚡',
    support: '🌊',
  }
};

export const ThemeProvider = ({ children }) => {
  const theme = THEME;

  // Apply CSS variables on mount
  useEffect(() => {
    const root = document.documentElement;
    
    // Primary colors
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-primary-light', theme.colors.primaryLight);
    root.style.setProperty('--color-primary-dark', theme.colors.primaryDark);
    
    // AI colors
    root.style.setProperty('--color-ai', theme.colors.ai);
    root.style.setProperty('--color-ai-light', theme.colors.aiLight);
    
    // Action colors
    root.style.setProperty('--color-action', theme.colors.action);
    root.style.setProperty('--color-action-light', theme.colors.actionLight);
    
    // Emotional colors
    root.style.setProperty('--color-emotional', theme.colors.emotional);
    root.style.setProperty('--color-emotional-light', theme.colors.emotionalLight);
    
    // Work colors
    root.style.setProperty('--color-work', theme.colors.work);
    
    // Health colors
    root.style.setProperty('--color-health', theme.colors.health);
    
    // Backgrounds
    root.style.setProperty('--color-background', theme.colors.background);
    root.style.setProperty('--color-card-bg', theme.colors.cardBg);
    
    // Legacy
    root.style.setProperty('--color-accent', theme.colors.accent);
    root.style.setProperty('--color-accent-light', theme.colors.accentLight);
    root.style.setProperty('--color-logo', theme.colors.logo);
  }, [theme]);

  // Helper to get mode-specific color
  const getModeColor = (mode) => {
    return theme.modes[mode] || theme.colors.primary;
  };

  const value = {
    theme,
    themeId: 'lifeflo',
    themes: { lifeflo: THEME },
    changeTheme: () => {}, // No-op - single theme
    showThemePicker: false,
    setShowThemePicker: () => {}, // No-op
    // Helper functions
    getEmoji: (type) => theme.emojis[type] || theme.emojis.neutral,
    getColor: (colorKey) => theme.colors[colorKey],
    getModeColor,
    getGradient: () => theme.colors.gradient,
    getGradientBg: () => theme.colors.gradientBg,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export THEMES for backward compatibility
export const THEMES = { lifeflo: THEME };

export default ThemeContext;
